import FbButton, { IFbButtonProps } from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import React from 'react'
import { IconType } from 'react-icons'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useToolbarDensity } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import { toolbarCountBadgeClass } from 'components/Tables/CommonTableCells/toolbarStyles'

export interface ITableButtonProps extends IFbButtonProps {
  icon?: React.ElementType | IconType
  text?: string
  activeValue?: string | null
  iconSize?: number
  /** Trailing icon rendered at the right edge (after text + activeValue). Use
   *  a caret (`PiCaretDown`) when the button opens a dropdown, so it reads as a
   *  select — matches the Replies-tab filter triggers. Always visible (unlike
   *  the text, which hides below xl). */
  trailingIcon?: React.ElementType | IconType

  postpendIcon?: React.ElementType | IconType
  onPostpendIconClick?: () => void
  /** Opt out of the default xl:min-w-[140px] / xl:min-w-[120px] so the
   *  button sizes to its content. DS2026 surfaces hug content by default
   *  so the search bar grows; pass `hugContent={false}` to force the
   *  legacy uniform-width behavior. */
  hugContent?: boolean
}

const TableButton = React.forwardRef((props: ITableButtonProps, ref: any) => {
  const {
    icon,
    text,
    activeValue,
    iconSize,
    trailingIcon,
    postpendIcon,
    onPostpendIconClick,
    hugContent,
    ...rest
  } = props
  const variant = useTableLinkVariant()
  // DS2026 (variant="plain") surfaces default to hug-content so Filter /
  // Sort / Columns buttons stay tight and the search bar fills the row.
  // Legacy surfaces keep the historical uniform 140px min-width unless
  // the caller opts in.
  const shouldHugContent = hugContent ?? variant === 'plain'
  // Compact toolbars shrink the button so it lines up with the matching search
  // shell — sm = 32px (Targets/Deals/Contacts), xs = 28px (Campaigns/Replies).
  // Heights are explicit px values, not Tailwind h-8/h-7: the app's root
  // font-size is 14px (main.css), so rem-based heights render smaller than their
  // nominal px and would sit short of the px-height search shell.
  const density = useToolbarDensity()
  const isCompact = density !== 'md'
  // Height class shared by the wrapper min-height, the button, and the postpend.
  const compactHeightClass =
    density === 'xs'
      ? '!h-[28px] !min-h-[28px]'
      : density === 'sm'
        ? '!h-[32px] !min-h-[32px]'
        : ''
  const wrapperMinHeight =
    density === 'xs'
      ? 'min-h-[28px]'
      : density === 'sm'
        ? 'min-h-[32px]'
        : 'min-h-[38px]'
  // Default the icon proportional to the button; legacy md keeps 24. An explicit
  // iconSize from the caller always wins.
  const resolvedIconSize =
    iconSize ?? (density === 'xs' ? 16 : density === 'sm' ? 18 : 24)

  const renderIcon = (
    IconComponent: React.ElementType | IconType | undefined,
    size: number
  ) => {
    if (!IconComponent) return null
    return <IconComponent size={size} />
  }

  // DS2026 outline button: Gray/300 border, Gray/800 text, hover Gray/100 bg
  // with Gray/900 text. Overrides FbButton secondary's #EAECF0 / #191D23 chrome.
  const ds2026 =
    variant === 'plain'
      ? '!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900'
      : ''

  return (
    <div
      data-table-button=""
      className={cn(
        'flex',
        wrapperMinHeight,
        !shouldHugContent && 'xl:min-w-[140px]'
      )}
    >
      <FbButton
        ref={ref}
        variant="secondary"
        className={cn(
          {
            active: activeValue !== null,
            'rounded-r-none': !!postpendIcon,
          },
          'bg-white gap-2 justify-start text-nowrap flex-1',
          isCompact && cn(compactHeightClass, '!text-sm'),
          !shouldHugContent && 'xl:min-w-[120px]',
          ds2026
        )}
        {...rest}
      >
        {renderIcon(icon, resolvedIconSize)}
        {/* Below xl the label normally collapses so the button reads as
            icon-only — but only when there IS an icon to fall back on. An
            icon-less button (e.g. the campaigns "Status" filter) keeps its
            label at every width so it never renders empty. */}
        <span className={icon ? 'hidden xl:inline' : undefined}>
          {text}
          {activeValue && (
            <span
              className={cn(
                'ml-1.5 inline-block align-middle',
                toolbarCountBadgeClass
              )}
            >
              {activeValue}
            </span>
          )}
        </span>
        {trailingIcon && (
          <span className="ml-0.5 flex-shrink-0 text-neutral-500">
            {renderIcon(trailingIcon, 14)}
          </span>
        )}
      </FbButton>
      {!!postpendIcon && (
        <FbButton
          variant={'primary'}
          className={cn(
            'rounded-l-none rounded-r-md px-2 -ml-1',
            // Match the main button's compact height so the save affordance
            // doesn't tower over it in a compact toolbar (FbButton's default
            // min-h-10 is ~35px at the 14px root).
            isCompact && compactHeightClass,
            variant === 'plain' && '!bg-brand-bright hover:!bg-brand-active'
          )}
          onClick={onPostpendIconClick}
        >
          {renderIcon(postpendIcon, resolvedIconSize)}
        </FbButton>
      )}
    </div>
  )
})
TableButton.displayName = 'TableButton'
export default TableButton
